/**
 * Deliverable presets in the Suite's screens (SafeShare slice 6.1;
 * docs/safeshare/design/01-data-model.md section 2.6).
 *
 *   usePresets({includeInactive, enabled})   loads the presets once (listPresets:
 *                                            admins and rig operators; an
 *                                            operator gets the active ones only)
 *   PresetPicker                             ticks up to four presets: a job's
 *                                            default, or a subject's own pick
 *
 * Nothing here writes: the job default goes through setJobAutomation
 * (JobPresets.jsx), a subject's pick through setSubjectPresets
 * (handheld/subjectForm.jsx), the records through the admin screen
 * (apps/safeshare/views/PresetsAdmin.jsx).
 */
import React, { useEffect, useState } from "react";
import { listPresets } from "../lib/presetCallables.js";
import { C, font } from "../lib/styles.js";

/** The most presets one pick names (the server checks it too: MAX_PICKED_PRESETS). */
export const MAX_PICKED = 4;

export function usePresets({ includeInactive = false, enabled = true } = {}) {
  const [state, setState] = useState({ loading: enabled, error: null, presets: [] });
  useEffect(() => {
    if (!enabled) {
      setState({ loading: false, error: null, presets: [] });
      return undefined;
    }
    let live = true;
    setState({ loading: true, error: null, presets: [] });
    listPresets({ includeInactive })
      .then((r) => { if (live) setState({ loading: false, error: null, presets: r?.presets ?? [] }); })
      .catch((e) => { if (live) setState({ loading: false, error: e?.message || "Could not load the presets.", presets: [] }); });
    return () => { live = false; };
  }, [includeInactive, enabled]);
  return state;
}

/** The required exports as one line: "40M OBJ · 5M OBJ · 100K OBJ". */
export const presetLadder = (preset) => (preset.exports ?? []).filter((e) => e.required)
  .map((e) => `${e.label.toUpperCase()} ${e.format.toUpperCase()}`).join(" · ");

export function PresetPicker({ presets = [], value = [], onChange, disabled = false, emptyLabel }) {
  // The active presets, and any already picked that were switched off since (shown, so they can be unticked).
  const shown = presets.filter((p) => p.active || value.includes(p.presetId));
  const unknown = value.filter((id) => !presets.some((p) => p.presetId === id));
  const full = value.length >= MAX_PICKED;
  const toggle = (id) => {
    const checked = value.includes(id);
    if (disabled || (!checked && full)) return;
    onChange(checked ? value.filter((x) => x !== id) : [...value, id]);
  };
  return (
    <div style={{ fontFamily: font, fontSize: 12, color: C.text }}>
      {!value.length && <div style={{ color: C.muted, marginBottom: 6 }}>{emptyLabel}</div>}
      {shown.map((p) => {
        const checked = value.includes(p.presetId);
        return (
          <label key={p.presetId} style={{ display: "flex", gap: 7, alignItems: "baseline", marginBottom: 5 }}>
            <input type="checkbox" checked={checked} disabled={disabled || (!checked && full)} onChange={() => toggle(p.presetId)} />
            <span>
              {p.name}{!p.active && <span style={{ color: C.muted }}> (switched off)</span>}{" "}
              <span style={{ color: C.muted }}>{presetLadder(p)}</span>
            </span>
          </label>
        );
      })}
      {unknown.map((id) => (
        <label key={id} style={{ display: "flex", gap: 7, alignItems: "baseline", marginBottom: 5 }}>
          <input type="checkbox" checked disabled={disabled} onChange={() => toggle(id)} />
          <span style={{ color: C.muted }}>{id} (no such preset)</span>
        </label>
      ))}
      {full && <div style={{ color: C.muted, marginBottom: 6 }}>At most {MAX_PICKED} presets.</div>}
      {!!value.length && (
        <button type="button" className="hh-btn ghost" disabled={disabled} onClick={() => onChange([])}>
          Clear: {emptyLabel}
        </button>
      )}
    </div>
  );
}
export default PresetPicker;
